import { ChangeDetectorRef, Component, DestroyRef, inject, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { getRoleIcon } from '@app/v2/settings/components/manage-team/constants/role-icons'
import { ROLES_PRIVILEGE_TABLE_MODAL_ID } from '@app/v2/settings/components/manage-team/roles-table/roles-table.component'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { EmailInputComponent } from '@app/v2/shared/components/inputs/email-input/email-input.component'
import { FullnameComponent } from '@app/v2/shared/components/inputs/fullname/fullname.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { TextAreaComponent } from '@app/v2/shared/components/text-area/text-area.component'
import { listenLayeredOverlayDismiss } from '@app/v2/shared/helpers/layered-overlay-dismiss'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { FullnameParts, InviteTeamMemberPayload, USER_ROLES } from '@app/v2/shared/types/user.type'
import { catchError, EMPTY, finalize, map, merge, Subject, switchMap, tap } from 'rxjs'

export const INVITE_TEAM_MEMBERS_MODAL_ID = 'invite-team-members-modal'

type RoleOption = {
  id: USER_ROLES
  name: string
  icon: string
  disabled?: boolean
}

const INVITABLE_ROLES: USER_ROLES[] = [USER_ROLES.ADMIN, USER_ROLES.COLLABORATOR, USER_ROLES.VIEWER, USER_ROLES.ACCOUNTANT]

@Component({
  selector: 'app-invite-team-members-modal',
  imports: [ModalComponent, FullnameComponent, EmailInputComponent, SelectInputComponent, TextAreaComponent, ProgressCircleComponent],
  standalone: true,
  templateUrl: './invite-team-members-modal.component.html',
  styleUrl: './invite-team-members-modal.component.scss'
})
export class InviteTeamMembersModalComponent implements OnInit, OnDestroy {
  public readonly closed = output<void>()
  public readonly invited = output<InviteTeamMemberPayload>()
  public readonly modalId = INVITE_TEAM_MEMBERS_MODAL_ID
  public readonly roles = signal<RoleOption[]>([])
  public readonly headerIcon = signal<string>(`<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_35137_53463)">
    <path d="M11.3333 5.33333H6.66667V0.666667C6.66667 0.489856 6.59643 0.320286 6.4714 0.195262C6.34638 0.0702379 6.17681 0 6 0C5.82319 0 5.65362 0.0702379 5.5286 0.195262C5.40357 0.320286 5.33333 0.489856 5.33333 0.666667V5.33333H0.666667C0.489856 5.33333 0.320286 5.40357 0.195262 5.5286C0.0702379 5.65362 0 5.82319 0 6C0 6.17681 0.0702379 6.34638 0.195262 6.4714C0.320286 6.59643 0.489856 6.66667 0.666667 6.66667H5.33333V11.3333C5.33333 11.5101 5.40357 11.6797 5.5286 11.8047C5.65362 11.9298 5.82319 12 6 12C6.17681 12 6.34638 11.9298 6.4714 11.8047C6.59643 11.6797 6.66667 11.5101 6.66667 11.3333V6.66667H11.3333C11.5101 6.66667 11.6797 6.59643 11.8047 6.4714C11.9298 6.34638 12 6.17681 12 6C12 5.82319 11.9298 5.65362 11.8047 5.5286C11.6797 5.40357 11.5101 5.33333 11.3333 5.33333Z" fill="#111928"/>
    </g>
    <defs>
    <clipPath id="clip0_35137_53463">
    <rect width="12" height="12" fill="white"/>
    </clipPath>
    </defs>
    </svg>
`)

  public readonly isValidatingEmail = signal(false)
  public readonly isInviting = input<boolean>(false)
  public readonly openRolesPrivilegeTable = output<void>()
  public readonly closeRolesPrivilegeTable = output<void>()
  private readonly _fullnameInput = viewChild(FullnameComponent)
  private readonly _authService = inject(AuthService)
  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _modalService = inject(ModalService)
  private readonly _organizationService = inject(OrganizationService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _changeDetectorRef = inject(ChangeDetectorRef)
  private readonly _validateEmail$ = new Subject<string>()
  private _lastValidEmail: string | null = null
  private _previousEmailValue = ''
  private _lastPreviewedGreetingName: string | null = null
  private _unlistenLayeredOverlayDismiss?: () => void
  private static readonly _firstNamePlaceholder = '{{firstName}}'
  private static readonly _salutationsPlaceholder = '{{salutations}}'
  private static readonly _greetingPlaceholders = '{{salutations}} {{firstName}}'
  private static readonly _organizationNamePlaceholder = '{{Organization name}}'

  public readonly memberForm = signal<FormGroup>(
    new FormGroup({
      fullname: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      role: new FormControl('', [Validators.required]),
      message: new FormControl(this._getDefaultMessage(), [Validators.maxLength(250)])
    })
  )

  ngOnInit(): void {
    this._openModal()
    this._listenToEmailControlChanges()
    this._listenToFullnameControlChanges()
    this._setupEmailValidation()
    this._initializeRoles()
    this._listenForLayeredOverlayDismiss()
  }

  ngOnDestroy(): void {
    this._unlistenLayeredOverlayDismiss?.()
    this._modalService.unregister(this.modalId)
  }

  /**
   * Notifies the parent to destroy this component after the modal closes.
   */
  public onModalClosed(): void {
    this.closed.emit()
  }

  /**
   * Validates the invited email against the organization on blur.
   */
  public onEmailBlur(): void {
    const emailControl = this.memberForm().get('email') as FormControl
    const email = (emailControl.value ?? '').trim()

    emailControl.markAsTouched()

    if (!email || this._hasClientEmailErrors(emailControl)) return

    const organizationId = this._authService.currentWorkspace?.id
    if (!organizationId) return

    if (email === this._lastValidEmail) return

    this._validateEmail$.next(email)
  }

  /**
   * Reacts to fullname changes and previews salutations + first name in the invitation message.
   * Uses FullnameComponent parsing (same as the fullname field) for greeting parts.
   * When the name is cleared, restores {{firstName}} in the Hi greeting (editor never shows {{salutations}}).
   */
  private _listenToFullnameControlChanges(): void {
    const fullnameControl = this.memberForm().controls['fullname'] as FormControl
    const messageControl = this.memberForm().controls['message'] as FormControl

    fullnameControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      const greetingName = this._buildGreetingPreviewFromParts(this._buildFullnameParts(String(value ?? '')))
      const currentMessage = String(messageControl.value ?? '')
      const updatedMessage = greetingName ? this._applyGreetingToMessage(currentMessage, greetingName) : this._restoreEditorGreetingPlaceholder(currentMessage)

      this._lastPreviewedGreetingName = greetingName || null
      if (updatedMessage === currentMessage) return

      messageControl.setValue(updatedMessage, { emitEvent: false })
      this._changeDetectorRef.markForCheck()
    })
  }

  /**
   * Emits invite payload to parent; parent performs the API call.
   * Template messages keep {{salutations}} {{firstName}} in the payload; fully custom text is sent as typed.
   * Includes the same fullname parts object produced by FullnameComponent.processString.
   */
  public onInviteClick(): void {
    const { fullname, email, role, message } = this.memberForm().getRawValue()
    this.memberForm().markAllAsTouched()
    if (this.isValidatingEmail() || this.isInviting() || this.memberForm().invalid) return

    const trimmedFullname = String(fullname).trim()
    const fullnameParts = this._buildFullnameParts(trimmedFullname)

    this.invited.emit({
      email: String(email).trim(),
      role: role as USER_ROLES,
      message: this._resolveInviteMessage(String(message ?? ''), trimmedFullname),
      fullname: fullnameParts
    })
  }

  /**
   * Opens the invite modal when it is not already visible.
   */
  private _openModal(): void {
    if (!this._modalService.isModalOpen(this.modalId)) {
      this._modalService.toggleModal(this.modalId)
    }
  }

  /**
   * Reacts to email value or validation status changes.
   */
  private _listenToEmailControlChanges(): void {
    const emailControl = this.memberForm().controls['email'] as FormControl
    this._previousEmailValue = (emailControl.value ?? '').trim()

    merge(emailControl.valueChanges, emailControl.statusChanges)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._onEmailControlChange(emailControl))
  }

  /**
   * Clears cached validation and server errors when the email value changes.
   */
  private _onEmailControlChange(emailControl: FormControl): void {
    const email = (emailControl.value ?? '').trim()
    const valueChanged = email !== this._previousEmailValue

    if (valueChanged) {
      this._previousEmailValue = email

      if (this._lastValidEmail !== null && email !== this._lastValidEmail) {
        this._lastValidEmail = null
      }

      this._organizationService.handleServerValidationErrors(this.memberForm())
    }

    this._changeDetectorRef.markForCheck()
  }

  /**
   * Validates the invited email against the organization on blur.
   */
  private _setupEmailValidation(): void {
    this._validateEmail$
      .pipe(
        tap(() => this.isValidatingEmail.set(true)),
        switchMap((email) => this._validateEmailOnServer(email)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(({ email, result }) => this._handleEmailValidationResult(email, result))
  }

  /**
   * Calls the API to validate the invited email for the current organization.
   */
  private _validateEmailOnServer(email: string) {
    const organizationId = this._authService.currentWorkspace?.id
    if (!organizationId) return EMPTY

    return this._organizationService.validateInvitedTeamMemberEmail(organizationId, email).pipe(
      map((result) => ({ email, result })),
      catchError(() => EMPTY),
      finalize(() => {
        this.isValidatingEmail.set(false)
        this._changeDetectorRef.markForCheck()
      })
    )
  }

  /**
   * Applies server-side email validation results to the form.
   */
  private _handleEmailValidationResult(email: string, result: { valid: boolean; errors?: Record<string, string[]> }): void {
    if (result.valid === false) {
      const fieldErrors = result.errors?.['errors'] ?? result.errors
      this._organizationService.handleServerValidationErrors(this.memberForm(), fieldErrors, false)
    } else {
      this._lastValidEmail = email
      this._organizationService.handleServerValidationErrors(this.memberForm())
    }

    this._changeDetectorRef.markForCheck()
  }

  /**
   * Builds the role options shown in the invite form.
   */
  private _initializeRoles(): void {
    const actorRole = this._workspaceRoleService.getUserRoleFromWorkspaceUsers(this._authService.currentWorkspace?.workspace_users || [])
    const roleOptions = this._buildRoleOptions(actorRole)
    const roleControl = this.memberForm().get('role') as FormControl

    this.roles.set(roleOptions)

    if (!actorRole) {
      roleControl.setValue('')
      roleControl.disable()
      return
    }

    if (actorRole === USER_ROLES.COLLABORATOR) {
      roleControl.setValue(USER_ROLES.VIEWER)
      roleControl.disable()
      return
    }

    roleControl.enable()
    const allowedRoles = new Set(roleOptions.map((option) => option.id))
    if (roleControl.value && !allowedRoles.has(roleControl.value as USER_ROLES)) {
      roleControl.setValue('')
    }
  }

  /**
   * Determines available role options for invitation based on actor role.
   * @param {string | null} actorRole The role of the actor user
   * @returns {USER_ROLES[]} The invitable roles
   */
  private _getInvitableRoles(actorRole: string | null): USER_ROLES[] {
    if (!actorRole) return []

    if (this._workspaceRoleService.isSuperAdmin() || actorRole === USER_ROLES.OWNER || actorRole === USER_ROLES.ADMIN) {
      return INVITABLE_ROLES
    }

    if (actorRole === USER_ROLES.COLLABORATOR) return [USER_ROLES.VIEWER]

    return []
  }

  /**
   * Builds role options and disabled states for the invite dropdown.
   * @param {string | null} actorRole The role of the actor user
   * @returns {RoleOption[]} The role options
   */
  private _buildRoleOptions(actorRole: string | null): RoleOption[] {
    if (!actorRole) return []

    if (actorRole === USER_ROLES.COLLABORATOR) {
      return INVITABLE_ROLES.map((role, index) => ({
        id: role,
        name: this._formatRole(role),
        icon: getRoleIcon(role, index),
        disabled: role !== USER_ROLES.VIEWER
      }))
    }

    const invitableRoles = this._getInvitableRoles(actorRole)
    return invitableRoles.map((role, index) => ({
      id: role,
      name: this._formatRole(role),
      icon: getRoleIcon(role, index)
    }))
  }

  /**
   * Returns true when the email control has client-side validation errors.
   */
  private _hasClientEmailErrors(control: FormControl): boolean {
    if (!control.validator) return false

    const validationErrors = control.validator(control)
    return !!validationErrors && Object.keys(validationErrors).length > 0
  }

  /**
   * Capitalizes the first letter of a role for display.
   * @param {USER_ROLES} role Role to format
   * @returns {string} Formatted role
   */
  private _formatRole(role: USER_ROLES): string {
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
  }

  /**
   * Builds the default invitation message shown in the editor ({{salutations}} is payload-only).
   */
  private _getDefaultMessage(): string {
    return `Hi ${InviteTeamMembersModalComponent._firstNamePlaceholder},\n\nJoin me to collaborate on our events on MICEtribe platform.`
  }

  /**
   * Builds the payload message: template keeps {{salutations}} {{firstName}}; fully custom text is sent literally.
   * Converts newlines to <br> for the API.
   * @param message Invitation message from the form
   * @param fullname Invitee full name (used to detect whether the greeting slot is still present)
   */
  private _resolveInviteMessage(message: string, fullname: string): string {
    const greetingName = this._buildGreetingPreviewFromParts(this._buildFullnameParts(fullname))
    const workspaceName = this._authService.currentWorkspace?.name ?? ''

    let resolved = this._isFullyCustomInviteMessage(message, greetingName) ? message : this._restoreGreetingPlaceholdersInMessage(message, greetingName)

    if (workspaceName && resolved.includes(InviteTeamMembersModalComponent._organizationNamePlaceholder)) {
      resolved = resolved.replaceAll(InviteTeamMembersModalComponent._organizationNamePlaceholder, workspaceName)
    }
    return resolved.replaceAll('\n', '<br>')
  }

  /**
   * True when the user replaced the template entirely with custom text that does not use the greeting slot.
   * @param message Current invitation message
   * @param greetingName Parsed salutation + first name preview
   */
  private _isFullyCustomInviteMessage(message: string, greetingName: string): boolean {
    if (message.includes(InviteTeamMembersModalComponent._firstNamePlaceholder)) return false
    if (message.includes(InviteTeamMembersModalComponent._salutationsPlaceholder)) return false
    if (this._messageContainsGreetingName(message, greetingName)) return false

    const normalized = this._normalizeInviteMessage(message)
    const defaultMessage = this._normalizeInviteMessage(this._getDefaultMessage())
    if (normalized === defaultMessage) return false

    return true
  }

  /**
   * Returns unique non-empty greeting names currently tracked for message preview/payload.
   * @param greetingName Parsed salutation + first name preview
   */
  private _getTrackedGreetingNames(greetingName: string): string[] {
    return [...new Set([greetingName, this._lastPreviewedGreetingName].filter((name): name is string => !!name?.trim()))]
  }

  /**
   * True when the invitee greeting name (current or last previewed) appears in the message.
   * @param message Current invitation message
   * @param greetingName Parsed salutation + first name preview
   */
  private _messageContainsGreetingName(message: string, greetingName: string): boolean {
    return this._getTrackedGreetingNames(greetingName).some((name) => new RegExp(`(?:^|\\s)${this._escapeRegExp(name)}(?=\\s|,|$)`).test(message))
  }

  /**
   * Swaps previewed greeting names back to {{salutations}} {{firstName}} for the API payload.
   * @param message Current invitation message
   * @param greetingName Parsed salutation + first name preview
   */
  private _restoreGreetingPlaceholdersInMessage(message: string, greetingName: string): string {
    const salutationsPlaceholder = InviteTeamMembersModalComponent._salutationsPlaceholder
    const firstNamePlaceholder = InviteTeamMembersModalComponent._firstNamePlaceholder
    const greetingPlaceholders = InviteTeamMembersModalComponent._greetingPlaceholders

    let resolved = message
    if (resolved.includes(salutationsPlaceholder) && resolved.includes(firstNamePlaceholder)) {
      return resolved
    }

    for (const name of this._getTrackedGreetingNames(greetingName)) {
      resolved = resolved.replace(new RegExp(`(^Hi\\s+)${this._escapeRegExp(name)}(\\s*,)`, 'i'), `$1${greetingPlaceholders}$2`)
    }

    if (resolved.includes(firstNamePlaceholder) && !resolved.includes(salutationsPlaceholder)) {
      resolved = resolved.replaceAll(firstNamePlaceholder, greetingPlaceholders)
    }

    return resolved
  }

  /**
   * Normalizes message text for default-template comparison.
   * @param message Raw invitation message
   */
  private _normalizeInviteMessage(message: string): string {
    return message.replace(/\r\n/g, '\n').trim()
  }

  /**
   * Escapes a string for safe use inside a RegExp.
   * @param value Raw string
   */
  private _escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  /**
   * Updates the message preview with salutation + first name in the Hi greeting slot only.
   * @param message Current invitation message
   * @param greetingName Salutation + first name shown in the preview
   */
  private _applyGreetingToMessage(message: string, greetingName: string): string {
    const salutationsPlaceholder = InviteTeamMembersModalComponent._salutationsPlaceholder
    const firstNamePlaceholder = InviteTeamMembersModalComponent._firstNamePlaceholder

    if (message.includes(salutationsPlaceholder) || message.includes(firstNamePlaceholder)) {
      let resolved = message
      if (resolved.includes(salutationsPlaceholder) && resolved.includes(firstNamePlaceholder)) {
        resolved = resolved.replace(new RegExp(`${this._escapeRegExp(salutationsPlaceholder)}\\s*${this._escapeRegExp(firstNamePlaceholder)}`, 'g'), greetingName)
      }
      if (resolved.includes(firstNamePlaceholder)) {
        resolved = resolved.replaceAll(firstNamePlaceholder, greetingName)
      }
      if (resolved.includes(salutationsPlaceholder)) {
        resolved = resolved.replaceAll(salutationsPlaceholder, '')
      }
      return resolved.replace(/Hi\s{2,}/i, 'Hi ')
    }

    if (!this._lastPreviewedGreetingName) return message

    return message.replace(new RegExp(`(^Hi\\s+)${this._escapeRegExp(this._lastPreviewedGreetingName)}(\\s*,)`, 'i'), `$1${greetingName}$2`)
  }

  /**
   * Puts {{firstName}} back in the Hi greeting when the full name field is cleared.
   * Editor never surfaces {{salutations}}; that is injected only in the API payload.
   * @param message Current invitation message
   */
  private _restoreEditorGreetingPlaceholder(message: string): string {
    const firstNamePlaceholder = InviteTeamMembersModalComponent._firstNamePlaceholder
    if (message.includes(firstNamePlaceholder) || !this._lastPreviewedGreetingName) return message

    return message.replace(new RegExp(`(^Hi\\s+)${this._escapeRegExp(this._lastPreviewedGreetingName)}(\\s*,)`, 'i'), `$1${firstNamePlaceholder}$2`)
  }

  /**
   * Builds the fullname parts object using FullnameComponent.processString (same as the fullname field).
   * Applies the same single-given-name firstName fallback used by fullname segment display.
   * @param fullname Raw full name from the form
   */
  private _buildFullnameParts(fullname: string): FullnameParts {
    const parsed = this._fullnameInput()?.processString(fullname) ?? {
      salutations: null,
      firstName: null,
      middleName: null,
      lastName: null
    }

    const parts: FullnameParts = {
      salutations: parsed['salutations'] ?? null,
      firstName: parsed['firstName'] ?? null,
      middleName: parsed['middleName'] ?? null,
      lastName: parsed['lastName'] ?? null
    }

    // Mirror FullnameComponent display fallback: one given name after salutations → firstName.
    if (!parts.firstName && fullname.trim()) {
      const words = fullname.trim().split(/\s+/).filter(Boolean)
      const salutationCount = parts.salutations ? parts.salutations.split(' ').filter(Boolean).length : 0
      const remaining = words.slice(salutationCount)
      if (remaining.length >= 1) {
        parts.firstName = remaining[0]
      }
    }

    return parts
  }

  /**
   * Builds the greeting preview string from FullnameComponent parts: salutation + first name.
   * Empty when there is no first name yet.
   * @param parts Parsed fullname parts from the fullname field
   */
  private _buildGreetingPreviewFromParts(parts: FullnameParts): string {
    const firstName = parts.firstName?.trim() ?? ''
    if (!firstName) return ''
    const title = parts.salutations?.trim() ?? ''
    return title ? `${title} ${firstName}` : firstName
  }

  /**
   * Asks the parent to open the roles privilege table over this invite modal.
   */
  public onOpenRolesPrivilegeTable(): void {
    this.openRolesPrivilegeTable.emit()
  }

  /**
   * Owns Escape dismiss so the roles table closes before this invite modal.
   */
  private _listenForLayeredOverlayDismiss(): void {
    this._unlistenLayeredOverlayDismiss = listenLayeredOverlayDismiss({
      isInnerOpen: () => this._modalService.isModalOpen(ROLES_PRIVILEGE_TABLE_MODAL_ID),
      closeInner: () => this._closeRolesPrivilegeTable(),
      closeParent: () => this._closeInviteModal(),
      destroyRef: this._destroyRef
    })
  }

  /**
   * Closes the stacked roles privilege table without dismissing the invite modal.
   */
  private _closeRolesPrivilegeTable(): void {
    if (this._modalService.isModalOpen(ROLES_PRIVILEGE_TABLE_MODAL_ID)) {
      this._modalService.toggleModal(ROLES_PRIVILEGE_TABLE_MODAL_ID)
    }
    this.closeRolesPrivilegeTable.emit()
  }

  /**
   * Closes the invite modal and notifies the parent to tear it down.
   */
  private _closeInviteModal(): void {
    if (this._modalService.isModalOpen(this.modalId)) {
      this._modalService.toggleModal(this.modalId)
    }
    this.closed.emit()
  }
}
